import { QueryClientProvider }          from '@tanstack/react-query';
import { ReactQueryDevtools }           from '@tanstack/react-query-devtools';
import { RouterProvider }               from '@tanstack/react-router';
import React, { useEffect, useState } from 'react';
import ReactDOM                         from 'react-dom/client';
import { Toaster }                      from 'sonner';

import { scheduleProactiveRefresh } from './lib/api';
import { tryEcocontSso } from './lib/ecocont-sso';
import { queryClient }   from './lib/query-client';
import { router }        from './router';
import { useAppStore }   from './store/app.store';
import './index.css';

function App() {
  const { theme } = useAppStore();

  // Arrancamos siempre en `false`, aunque `hasHydrated()` ya sea `true` en el
  // primer render — con localStorage la hidratación de zustand es SÍNCRONA,
  // así que esto pasa casi siempre. El código anterior asumía que solo pasaba
  // en casos raros y hacía `if (hydrated) return` en ese escenario, con lo
  // que tryEcocontSso() nunca se llamaba: el token del fragment
  // #eco_sso_token quedaba sin canjear y la app terminaba mostrando el login
  // (bug real reportado: "el token sí está en la URL pero no llama a sso").
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    void (async () => {
      if (!useAppStore.persist.hasHydrated()) {
        await new Promise<void>((resolve) => {
          useAppStore.persist.onFinishHydration(() => { resolve(); });
        });
      }
      // SSO desde ecocont_ui: si hay un token de ecocont en el fragment,
      // pisa lo que haya persistido.
      await tryEcocontSso();
      setHydrated(true);
      // Iniciar refresh proactivo con el token persistido o el de SSO
      scheduleProactiveRefresh();
    })();
  }, []);

  // Apply theme on mount / change
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    if (theme === 'system') {
      root.classList.add(window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    } else {
      root.classList.add(theme);
    }
  }, [theme]);

  if (!hydrated) return null;

  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster richColors position="top-right" />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

const root = document.getElementById('root');
if (!root) throw new Error('Root element not found');

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
